import { mergeAttributes, Node, nodeInputRule, type Command } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { IMAGE_INPUT_REGEX, VIDEO_INPUT_REGEX } from '@duke-hq/shared-constants/tiptap';

import { UploadFnType } from './mediaPasteDropPlugin/mediaPasteDropPlugin';

import { ResizableMediaNodeView } from './ResizableMediaNodeView';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableMedia: {
      /**
       * Set media
       */
      setMedia: (options: {
        'media-type': 'img' | 'video';
        src: string;
        alt?: string;
        title?: string;
        width?: string;
        height?: string;
      }) => ReturnType;
    };
  }
}

interface MediaOptions {
  // inline: boolean, // we have floating support, so block is good enough
  // allowBase64: boolean, // we're not going to allow this
  HTMLAttributes: Record<string, any>;
  uploadFn: UploadFnType;
  onVideoComplete?: any;
  onPlaying?: any;
  onReady?: () => void;
  autoPlay?: boolean;
}

export const ResizableMediaExtension = Node.create<MediaOptions>({
  name: 'resizableMedia',

  inline: false,

  group: 'block',

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      'media-type': {
        default: null,
      },
      personaId: {},
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: '300px',
      },
      height: {
        default: '150px',
      },
      file: {
        default: null,
      },
      simulation: {
        default: false,
      },
      fileDrn: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-drn'),
        renderHTML: (attributes) =>
          attributes.fileDrn ? { 'data-file-drn': attributes.fileDrn } : {},
      },
      placeholderId: {
        default: null,
      },
    };
  },

  selectable: true,

  parseHTML() {
    return [
      {
        tag: 'img[src]:not([src^="data:"])',
        getAttrs: (el) => ({
          src: (el as HTMLImageElement).getAttribute('src'),
          'media-type': 'img',
          width: (el as HTMLImageElement).getAttribute('width'),
          height: (el as HTMLImageElement).getAttribute('height'),
          fileDrn: (el as HTMLImageElement).getAttribute('data-file-drn'),
        }),
      },
      {
        tag: 'video',
        getAttrs: (el) => ({
          src: (el as HTMLVideoElement).getAttribute('src'),
          'media-type': 'video',
          width: (el as HTMLImageElement).getAttribute('width'),
          height: (el as HTMLImageElement).getAttribute('height'),
          fileDrn: (el as HTMLVideoElement).getAttribute('data-file-drn'),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { 'media-type': mediaType } = HTMLAttributes;

    if (mediaType === 'img') {
      return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    }
    if (mediaType === 'video') {
      return [
        'video',
        { controls: 'true', style: 'width: 100%', ...HTMLAttributes },
        ['source', HTMLAttributes],
      ];
    }

    if (!mediaType) {
      console.error(
        'TiptapMediaExtension-renderHTML method: Media Type not set, going default with image',
      );
    }

    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addCommands() {
    return {
      setMedia:
        (options): Command =>
        ({ commands }) => {
          const { 'media-type': mediaType } = options;

          if (mediaType === 'img') {
            return commands.insertContent({
              type: this.name,
              attrs: options,
            });
          }
          if (mediaType === 'video') {
            return commands.insertContent({
              type: this.name,
              attrs: {
                ...options,
                controls: 'true',
              },
            });
          }

          if (!mediaType) {
            console.error(
              'TiptapMediaExtension-setMedia: Media Type not set, going default with image',
            );
          }

          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableMediaNodeView);
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: IMAGE_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const [, , alt, src, title] = match;

          return {
            src,
            alt,
            title,
            'media-type': 'img',
          };
        },
      }),
      nodeInputRule({
        find: VIDEO_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const [, , src] = match;

          return {
            src,
            'media-type': 'video',
          };
        },
      }),
    ];
  },
});
